import { useEffect, useState } from 'react';
import { TIER_PRICING, type Tier } from '@vcard/shared';
import { useRoleLabel } from '../hooks/useRoleLabel.js';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { euro, seatUnitCents, type SeatPlan } from '../lib/seatPrices.js';

interface SeatUser {
  id: string;
  email: string;
  role: string;
  seatTier: Tier;
}

interface SeatData {
  seats: { starter: number; business: number };
  used: { starter: number; business: number };
  users: SeatUser[];
}

const PLANS: SeatPlan[] = ['starter', 'business'];

/** Owner view: which user holds a Starter and which a Business seat, with free seats and a bulk switch. */
export function SeatAssignment({ interval, onChanged }: { interval: 'month' | 'year' | null; onChanged?: () => void }) {
  const { t } = useAppTranslation();
  const roleLabel = useRoleLabel();
  const [data, setData] = useState<SeatData | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [bulk, setBulk] = useState<SeatPlan | null>(null);
  const [releaseOther, setReleaseOther] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    fetch('/api/billing/seats', { credentials: 'include' })
      .then((r) => (r.ok ? (r.json() as Promise<SeatData>) : null))
      .then((d) => { if (d) setData(d); })
      .catch(() => undefined);
  }, []);

  if (!data || data.seats.starter + data.seats.business === 0) return null;
  const { seats, used } = data;
  const free = { starter: seats.starter - used.starter, business: seats.business - used.business };
  const label = (p: SeatPlan) => TIER_PRICING[p].label;

  async function assign(userId: string, tier: SeatPlan) {
    setBusyId(userId);
    setError('');
    try {
      const res = await fetch(`/api/billing/seats/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier }),
      });
      const body = (await res.json().catch(() => ({}))) as SeatData & { error?: string };
      if (res.ok) { setData(body); onChanged?.(); }
      else setError(body.error ?? `HTTP ${res.status}`);
    } finally {
      setBusyId(null);
    }
  }

  async function assignAll(tier: SeatPlan) {
    setBulkBusy(true);
    setError('');
    try {
      const res = await fetch('/api/billing/seats/assign-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier, buyMissing: true, releaseOther }),
      });
      const body = (await res.json().catch(() => ({}))) as SeatData & { error?: string };
      if (res.ok) { setData(body); setBulk(null); onChanged?.(); }
      else { setError(body.error ?? `HTTP ${res.status}`); setBulk(null); }
    } finally {
      setBulkBusy(false);
    }
  }

  function buyMore() {
    document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // preview of the bulk switch
  const n = data.users.length;
  const target = bulk ?? 'business';
  const otherPlan: SeatPlan = target === 'starter' ? 'business' : 'starter';
  const after = { [target]: Math.max(seats[target], n), [otherPlan]: releaseOther ? 0 : seats[otherPlan] } as Record<SeatPlan, number>;
  const deltaCents = PLANS.reduce((sum, p) => sum + (after[p] - seats[p]) * seatUnitCents(p, interval), 0);
  const perInterval = interval === 'month' ? t('plans.perMonth', '/ Monat') : t('plans.perYear', '/ Jahr');
  const noFreeAtAll = PLANS.every((p) => free[p] < 1);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-ink">{t('seats.title', 'Plätze zuweisen')}</h2>
        <p className="text-xs text-ink-faint tabular-nums">
          {PLANS.map((p) => `${label(p)}: ${t('seats.freeOf', '{{free}} von {{total}} frei', { free: free[p], total: seats[p] })}`).join(' · ')}
        </p>
      </div>

      {n > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink-muted">{t('seats.allTo', 'Alle Nutzer auf')}</span>
          {PLANS.map((p) => (
            <button key={p} type="button" onClick={() => setBulk(p)}
              className="rounded-full border border-line px-3 py-1 text-sm font-medium text-ink hover:bg-raised transition-colors">
              {label(p)}
            </button>
          ))}
        </div>
      )}

      {error && <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{error}</p>}

      <ul className="divide-y divide-line rounded-xl border border-line bg-panel">
        {data.users.map((u) => {
          const current: SeatPlan = u.seatTier === 'business' ? 'business' : 'starter';
          return (
            <li key={u.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm text-ink">{u.email}</p>
                <p className="text-xs text-ink-faint">{roleLabel(u.role)}</p>
              </div>
              <select
                value={current}
                disabled={busyId === u.id}
                onChange={(e) => assign(u.id, e.target.value as SeatPlan)}
                aria-label={t('seats.planFor', 'Plan für {{email}}', { email: u.email })}
                className="rounded-lg border border-line bg-panel px-3 py-1.5 text-sm text-ink disabled:opacity-50"
              >
                {PLANS.map((p) => {
                  const blocked = p !== current && free[p] < 1;
                  return (
                    <option key={p} value={p} disabled={blocked}>
                      {label(p)}{blocked ? ` (${t('seats.noneFree', 'kein Platz frei')})` : ''}
                    </option>
                  );
                })}
              </select>
            </li>
          );
        })}
      </ul>

      {(noFreeAtAll || free.business < 1 || free.starter < 1) && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-line-strong bg-panel px-4 py-3">
          <p className="text-sm text-ink-muted">
            {free.business < 1 && free.starter < 1
              ? t('seats.allTaken', 'Alle Plätze sind vergeben. Kaufe weitere Plätze, um mehr Nutzer einzuladen oder höher zu stufen.')
              : free.business < 1
                ? t('seats.businessTaken', 'Alle Business-Plätze sind vergeben. Kaufe weitere Plätze, um mehr Nutzer auf Business zu setzen.')
                : t('seats.starterTaken', 'Alle Starter-Plätze sind vergeben. Kaufe weitere Plätze, um Nutzer auf Starter zu setzen.')}
          </p>
          <button type="button" onClick={buyMore} className="rounded-full bg-accent-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-accent-500 transition-colors">
            {t('seats.buyMore', 'Plätze kaufen')}
          </button>
        </div>
      )}
      <p className="text-xs text-ink-faint">{t('seats.hint', 'Neue Nutzer bekommen zunächst einen Starter-Platz. Business-Nutzer sehen Analytics, Leads und den Visitenkarten-Scan.')}</p>

      <ConfirmDialog
        open={bulk !== null}
        title={t('seats.bulkTitle', 'Alle Nutzer auf {{plan}} setzen?', { plan: label(target) })}
        confirmText={bulkBusy ? t('plans.loading', 'Lädt…') : t('seats.bulkConfirm', 'Ja, alle ändern')}
        cancelText={t('seats.cancel', 'Abbrechen')}
        busy={bulkBusy}
        onClose={() => setBulk(null)}
        onConfirm={() => assignAll(target)}
      >
        <p>{t('seats.bulkIntro', 'Das betrifft alle {{count}} Nutzer deiner Organisation. Es wird sofort umgestellt.', { count: n })}</p>
        <ul className="space-y-1 rounded-lg bg-raised px-3 py-2 tabular-nums">
          {PLANS.map((p) => (
            <li key={p} className="flex justify-between gap-3">
              <span>{label(p)}-{t('seats.seatsWord', 'Plätze')}</span>
              <span className="text-ink">{seats[p]} → {after[p]}</span>
            </li>
          ))}
        </ul>
        {seats[otherPlan] > 0 && (
          <label className="flex items-start gap-2 text-ink-muted">
            <input type="checkbox" checked={releaseOther} onChange={(e) => setReleaseOther(e.target.checked)} className="mt-0.5" />
            <span>{t('seats.releaseOther', 'Nicht mehr benötigte {{plan}}-Plätze kündigen', { plan: label(otherPlan) })}</span>
          </label>
        )}
        <p className="text-ink">
          {deltaCents === 0
            ? t('seats.noCost', 'Die Kosten ändern sich nicht.')
            : t('seats.costChange', 'Preisänderung: {{sign}}{{amount}} {{interval}}, im laufenden Zeitraum anteilig berechnet.', {
                sign: deltaCents > 0 ? '+' : '−', amount: euro(deltaCents), interval: perInterval,
              })}
        </p>
      </ConfirmDialog>
    </div>
  );
}
